import React, { useState, useEffect, useMemo } from "react";
import {
  Input,
  Row,
  Card,
  Table,
  Spin,
  Button,
  Space,
  Form,
  Pagination,
} from "antd";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import ArrowLeft from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import { BOM } from "../../../services/definedMethod";

// ── Helpers ────────────────────────────────────────────────────────────────
const parseDateArray = (arr) => {
  if (!Array.isArray(arr) || arr.length < 3) return null;
  const [y, m, d] = arr;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

const fmt = (val) =>
  val != null ? val.toLocaleString("en-NG", { minimumFractionDigits: 2 }) : "—";

// ── Status badge ───────────────────────────────────────────────────────────
const STATUS_COLORS = {
  Pending: { bg: "#FFFAEB", color: "#B54708" },
  Active: { bg: "#ECFDF3", color: "#027A48" },
  Closed: { bg: "#F2F4F7", color: "#344054" },
  Matured: { bg: "#EFF8FF", color: "#175CD3" },
  Rejected: { bg: "#FEF3F2", color: "#B42318" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 99,
        fontSize: 12,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {status}
    </span>
  );
};

// ── Component ──────────────────────────────────────────────────────────────
const ManageTreasuryBillContracts = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData || {};

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [originalList, setOriginalList] = useState([]);
  const [loading, setLoading] = useState(false);

  const pageSize = 10;

  const columns = [
    {
      title: "Ref ID",
      dataIndex: "contractReferenceId",
      render: (v) => v ?? "—",
    },
    {
      title: "Client",
      dataIndex: "clientName",
      render: (v) => v ?? "—",
    },
    {
      title: "Product",
      dataIndex: "productName",
      render: (v) => v ?? "—",
    },
    {
      title: "Currency",
      dataIndex: "currency",
      render: (v) => v ?? "—",
    },
    {
      title: "Face Value",
      dataIndex: "faceValue",
      render: fmt,
    },
    {
      title: "Purchase Price",
      dataIndex: "purchasePrice",
      render: fmt,
    },
    {
      title: "Discount Rate",
      dataIndex: "discountRate",
      render: (v) => (v != null ? `${v}%` : "—"),
    },
    {
      title: "Issue Date",
      dataIndex: "issueDate",
      render: (v) => (Array.isArray(v) ? parseDateArray(v) : (v ?? "—")),
    },
    {
      title: "Maturity Date",
      dataIndex: "settlementDate",
      render: (v) => (Array.isArray(v) ? parseDateArray(v) : (v ?? "—")),
    },
    {
      title: "Tenor (Days)",
      dataIndex: "tenor",
      render: (v) => (v != null ? `${v} days` : "—"),
    },
    {
      title: "Status",
      dataIndex: "statusText",
      render: (v) => <StatusBadge status={v} />,
    },
  ];

  useEffect(() => {
    getContractList();
  }, []);

  useEffect(() => {
    if (BOM.HasPermission?.("CREATE_TB_CONTRACT"))
      rightAction(cnt.RIGHT_ADD_BTN);
  }, []);

  const getContractList = () => {
    setLoading(true);
    const body = { session };
    const url = `${apiConfig.GetTreasuryBillContract}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load treasury bill contracts.",
        );
        setLoading(false);
        return;
      }

      // Response is a plain array based on actual API response
      const items = Array.isArray(res)
        ? res
        : Array.isArray(res?.pageItems)
          ? res.pageItems
          : [];

      const mapped = items.map((el) => ({
        key: el.id,
        id: el.id,
        contractReferenceId: el.contractReferenceId,
        // Client details nested under el.client
        clientName: el.client?.displayName ?? el.clientId ?? "—",
        clientId: el.client?.id ?? el.clientId,
        // Product details nested under el.product
        productName: el.product?.productName ?? el.productId ?? "—",
        productId: el.product?.id ?? el.productId,
        // Currency nested
        currency: el.currency?.code ?? "—",
        faceValue: el.faceValue,
        purchasePrice: el.purchasePrice,
        discountRate: el.discountRate,
        tenor: el.tenor,
        issueDate: el.issueDate,
        settlementDate: el.settlementDate,
        maturityValue: el.maturityValue,
        // Status nested under el.status
        statusText: el.status?.text ?? el.status ?? "—",
        statusCode: el.status?.code ?? el.status,
      }));

      setOriginalList(mapped);
      setSelectedRow([]);
      setCurrentPage(1);
      setLoading(false);
    });
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return originalList;
    const q = searchTerm.toLowerCase();
    return originalList.filter(
      (item) =>
        item.contractReferenceId?.toLowerCase().includes(q) ||
        item.clientName?.toLowerCase().includes(q) ||
        item.productName?.toLowerCase().includes(q) ||
        item.currency?.toLowerCase().includes(q) ||
        item.statusText?.toLowerCase().includes(q),
    );
  }, [originalList, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const itemRender = (_, type, el) => {
    if (type === "prev" || type === "next") return;
    return el;
  };

  const onSelectItemAction = (selectedRowKeys, selectedRows) => {
    if (!selectedRowKeys || selectedRowKeys.length !== 1) {
      setSelectedRow([]);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });
      if (BOM.HasPermission?.("CREATE_TB_CONTRACT"))
        dispatch({ type: cnt.RIGHT_ADD_BTN });
      return;
    }
    setSelectedRow(selectedRows);

    const selected = selectedRows[0];
    const isEditable = ![
      "contractStatus.active",
      "contractStatus.matured",
      "contractStatus.closed",
      "contractStatus.rejected",
    ].includes(selected?.statusCode);

    if (BOM.HasPermission?.("READ_TB_CONTRACT"))
      dispatch({ type: cnt.RIGHT_DETAIL_BTN });

    if (BOM.HasPermission?.("UPDATE_TB_CONTRACT") && isEditable)
      dispatch({ type: cnt.RIGHT_EDIT_BTN });
  };

  const handleOnView = () => {
    if (rightBtn?.detailBtn === "primary")
      navigate("/view-treasury-bill-contract", { state: selectedRow[0]?.id });
  };
  const handleOnCreate = () => {
    if (rightBtn?.addBtn === "primary") navigate("/add-treasury-bill-contract");
  };
  const handleOnEdit = () => {
    if (rightBtn?.editBtn === "primary")
      navigate("/edit-treasury-bill-contract", { state: selectedRow[0]?.id });
  };

  return (
    <MainLayout
      isEnableRightSidebar
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
      pageItems={staticConfig.managetreasuryBillContract}
    >
      <TopContent
        pageTitle={labelConfig.treasuryBillContract}
        createPageTitle={labelConfig.treasuryBillContractListing}
      />

      <Card
        style={{ border: "1px solid #EAECF0", borderRadius: 20, marginTop: 12 }}
      >
        <Spin spinning={loading}>
          <Row
            style={{
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Form form={form}>
              <Space size={12}>
                <Input
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value || "");
                    setCurrentPage(1);
                  }}
                  placeholder="Search contracts…"
                  prefix={<SearchLg width={18} color="#667085" />}
                  style={{ width: 350, borderRadius: 10 }}
                />
              </Space>
            </Form>
          </Row>

          <Table
            rowSelection={{
              type: "checkbox",
              hideSelectAll: true,
              onChange: onSelectItemAction,
            }}
            columns={columns}
            dataSource={paginatedData}
            pagination={false}
            scroll={{ x: "max-content" }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            <Button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            >
              <ArrowLeft /> Previous
            </Button>
            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              showSizeChanger={false}
              itemRender={itemRender}
              onChange={setCurrentPage}
            />
            <Button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            >
              Next <ArrowRight />
            </Button>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(
  ManageTreasuryBillContracts,
);
